
import { BrowserModule } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountPlan } from '../../../accounting/model/account-plan';
import { AccountPlanService } from '../../../accounting/service/data/account-plan.service';
import { FormComponentBase } from '../../../core/component/forms/form-component.base';
import { DataService } from '../../../core/service/base/data.service';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { DeleteFormCommandHandler } from '../../../core/service/command/delete-form-command-handler.service';
import { FormCommandHandlerResolver } from '../../../core/service/command/form-command-handler-resolver.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { standardCommand } from '../../../core/model/command';
import { AppendixService } from '../../service/data/appendix.service';
import { Appendix, AppendixSection } from '../../model/appendix';
import { SelectionItem } from '../../../core/component/select/select.component';
import { appendixCommand } from '../../command/appendix.command';
import { AppendixCommandHandlerResolver } from '../../service/command/appendix-command-handler-resolver.service';
import { PreviewAppendixCommandHandler } from '../../service/command/preview-appendix-command-handler.service';
import { AppendixTypeKindToString, AppendixTypeKind } from '../../model/const/enums';
import { FormControlEx } from '../../../core/component/forms/form-control';

@Component({
    selector: 'app-appendix-form',
    templateUrl: './appendix-form.component.html',
    styleUrls: ['./appendix-form.component.scss'],
    providers: [
        DeleteFormCommandHandler,
        FormCommandHandlerResolver,
        PreviewAppendixCommandHandler,
        AppendixCommandHandlerResolver,
        { provide: DataService, useExisting: AppendixService },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppendixFormComponent extends FormComponentBase<Appendix> {
  constructor(
    private _fb: FormBuilder,
    private _fc: FormControlExFactory,
    private _accountPlanService: AccountPlanService,
    activatedRoute: ActivatedRoute,
    commandDispatcher: CommandDispatcher,
    handlerResolver: AppendixCommandHandlerResolver,
    service: AppendixService,
    messageService: MessageService,
    navigation: NavigationService
  ) {
    super(activatedRoute, service, messageService, navigation, commandDispatcher, handlerResolver);
    this.addMenuItemDefinition('CREATE_BTN', standardCommand.create);
    this.addMenuItemDefinition('APPENDIX.PREVIEW_COMMAND', appendixCommand.previewAppendix);

    for (let i = 1; i < 100; i++) {
      this.orderSelection.push(new SelectionItem(i.toString(), i));
    }
  }

  orderSelection: SelectionItem[] = [];
  protected createModel(): Appendix {
    return new Appendix();
  }

  protected prepareSave(): Appendix {
    let model: Appendix;
    if (this.isSelfDefined) {
      model = super.prepareSave();
      model.sections = this.sections.controls.map(c => Object.assign(new AppendixSection(), c.value));
    } else {
      model = super.prepareSave();
      model.sections = [];
    }

    return model;
  }

  appendixTypes = [
    { name: AppendixTypeKindToString(AppendixTypeKind.SelfDefined), value: AppendixTypeKind.SelfDefined },
    { name: AppendixTypeKindToString(AppendixTypeKind.RentalSpecification), value: AppendixTypeKind.RentalSpecification },
  ];

  protected getFormGroup(): FormGroup {
    if (this.dataContext.isNew) {
      this.dataContext.sortOrder = 1;
      this.dataContext.appendixType = AppendixTypeKind.SelfDefined;
      const section = new AppendixSection();
      section.sortOrder = 1;
      this.dataContext.sections.push(section);
    }
    this.sections = this._fb.array(this.getSectionForms());
    const form = this._fb.group({
      id: this._fc.createHidden(this.dataContext.id),
      title: this._fc.createWithOptions('APPENDIX.TITLE', this.dataContext.title, {
        helpText: 'APPENDIX.HELP_TEXT.TITLE',
        required: true,
        length: 100,
      }),
      sortOrder: this._fc.createMandatory('APPENDIX.SORTORDER', this.dataContext.sortOrder, 'APPENDIX.HELP_TEXT.SORTORDER'),
      useForDwelling: this._fc.create(
        'APPENDIX.USE_FOR_DWELLING',
        this.dataContext.useForDwelling,
        null,
        null,
        'APPENDIX.HELP_TEXT.USE_FOR_DWELLING'
      ),
      useForParking: this._fc.create(
        'APPENDIX.USE_FOR_PARKING',
        this.dataContext.useForParking,
        null,
        null,
        'APPENDIX.HELP_TEXT.USE_FOR_PARKING'
      ),
      realEstateLevelUsage: this._fc.create(
        'APPENDIX.LINK_TO_PROPERTY',
        this.dataContext.realEstateLevelUsage,
        null,
        null,
        'APPENDIX.HELP_TEXT.LINK_TO_PROPERTY'
      ),
      pageFooter: this._fc.createWithOptions('APPENDIX.PAGE_FOOTER', this.dataContext.pageFooter, {
        helpText: 'APPENDIX.HELP_TEXT.PAGE_FOOTER',
        length: 400,
      }),
      companyLogo: this._fc.create('APPENDIX.COMPANY_LOGO', this.dataContext.companyLogo, null, null, 'APPENDIX.HELP_TEXT.COMPANY_LOGO'),
      appendixType: this._fc.createWithOptions('APPENDIX.APPENDIX_TYPE', this.dataContext.appendixType, {
        isReadonly: !this.dataContext.isNew,
        helpText: 'APPENDIX.HELP_TEXT.APPENDIX_TYPE',
        required: true,
      }),
      sections: this.sections,
    });

    this.setupAppendixType(form);

    return form;
  }

  private setupAppendixType(form: FormGroup): void {
    this.AddSubscription(
      form.controls['appendixType'].valueChanges.subscribe(value => {
        if (value !== AppendixTypeKind.SelfDefined) {
          for (const form of this.sections.controls.map(c => c as FormGroup)) {
            form.controls['header'].clearValidators();
            form.controls['header'].updateValueAndValidity();
            form.controls['content'].clearValidators();
            form.controls['content'].updateValueAndValidity();
          }
        }
      })
    );
  }

  sections: FormArray;

  private get appendixTypeControl(): FormControlEx {
    return this.form?.controls['appendixType'] as FormControlEx;
  }

  get isSelfDefined(): boolean {
    let c = this.appendixTypeControl;
    return c ? c.value === AppendixTypeKind.SelfDefined : false;
  }

  removeSection(sectionIndex: number) {
    this.sections.removeAt(sectionIndex);
    this.form.markAsDirty();
  }

  get showDeleteSection(): boolean {
    return this.sections.controls.length > 1;
  }

  addSection() {
    if (!this.form.valid) {
      FormComponentBase.markAsTouched(this.form);
      this.updateValueAndValidityRecursive(this.form, false);
      this.showPageValidationError();
      return;
    }
    let order = 0;
    for (const form of this.sections.controls.map(c => c as FormGroup)) {
      if (form.controls['sortOrder'].value > order) {
        order = form.controls['sortOrder'].value;
      }
    }
    var model = new AppendixSection();
    model.sortOrder = order + 1;
    this.sections.push(this.createSectionForm(model));
  }

  getSectionForms(): FormGroup[] {
    const forms: FormGroup[] = [];
    let order = 1;
    for (const section of this.dataContext.sections) {
      section.sortOrder = order++;
      forms.push(this.createSectionForm(section));
    }
    return forms;
  }

  createSectionForm(section: AppendixSection) {
    return this._fb.group({
      id: this._fc.createHidden(section.id),
      header: this._fc.createWithOptions('APPENDIX.SECTION.HEADER', section.header, {
        helpText: 'APPENDIX.SECTION.HELP_TEXT.HEADER',
        length: 100,
        required: true,
      }),
      sortOrder: this._fc.createMandatory('APPENDIX.SECTION.SORTORDER', section.sortOrder, 'APPENDIX.SECTION.HELP_TEXT.SORTORDER'),
      content: this._fc.createWithOptions('APPENDIX.SECTION.CONTENT', section.content, {
        helpText: 'APPENDIX.SECTION.HELP_TEXT.CONTENT',
        length: 5000,
        required: true,
      }),
    });
  }

  protected init(data: Appendix) {
    super.init(data);
  }
}
